import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as htmlToImage from 'html-to-image';
import { Download, LayoutTemplate, Shield, Image as ImageIcon, Check, Search, Loader2, Moon, Sun, Briefcase, Type, History, Crop, LogIn, LogOut } from 'lucide-react';
import { THEMES, PATTERNS, PRESETS } from './data';
import { Badge, Theme, Pattern, IndustryPreset } from './types';
import { BannerPreview } from './components/BannerPreview';
import { LandingPage } from './components/LandingPage';
import { cn } from './lib/utils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './lib/firebase';

export default function App() {
  const [user, loading] = useAuthState(auth);
  
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme>(THEMES[0]);
  const [selectedPattern, setSelectedPattern] = useState<Pattern>(PATTERNS[1]); // Default to grid
  const [layout, setLayout] = useState<'compact' | 'centric'>('compact');
  const [fontFamily, setFontFamily] = useState<'sans' | 'display' | 'mono' | 'serif'>('sans');
  const [profileUrl, setProfileUrl] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [showProfilePic, setShowProfilePic] = useState(false);
  const [profilePicSource, setProfilePicSource] = useState<'google' | 'skills'>('google');
  const [fetchedAvatarSrc, setFetchedAvatarSrc] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [exportFormat, setExportFormat] = useState<'png' | 'jpeg'>('png');
  
  const [availableBadges, setAvailableBadges] = useState<Badge[]>([]);
  const [selectedBadges, setSelectedBadges] = useState<Badge[]>([]);
  const [userName, setUserName] = useState('');
  const [userTitle, setUserTitle] = useState('Cloud Learner & Technologist');
  
  const [showSocials, setShowSocials] = useState(false);
  const [socialHandles, setSocialHandles] = useState<{ linkedin?: string; github?: string; twitter?: string; }>({
    linkedin: '',
    github: '',
    twitter: ''
  });

  const [bannerSize, setBannerSize] = useState<'horizontal' | 'square' | 'vertical'>('horizontal');

  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiError, setAiError] = useState('');

  const [isExporting, setIsExporting] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const [bannerHeight, setBannerHeight] = useState(396);
  
  const [recentUrls, setRecentUrls] = useState<string[]>([]);
  
  // Load user data from Firestore
  useEffect(() => {
    if (user) {
      const loadUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.recentUrls) setRecentUrls(data.recentUrls);
            if (data.userName) setUserName(data.userName);
            if (data.userTitle) setUserTitle(data.userTitle);
            if (data.socialHandles) setSocialHandles(data.socialHandles);
            if (data.profileUrl) setProfileUrl(data.profileUrl);
            if (data.showProfilePic !== undefined) setShowProfilePic(data.showProfilePic);
            if (data.profilePicSource) setProfilePicSource(data.profilePicSource);
          }
        } catch (error) {
          console.error('Error loading user data', error);
        }
      };
      loadUserData();
    }
  }, [user]);
  
  // Save user data to Firestore
  const saveUserData = async (updates: any) => {
    if (!user) return;
    try {
      await setDoc(doc(db, 'users', user.uid), updates, { merge: true });
    } catch (error) {
      console.error('Error saving user data', error);
    }
  };

  // Debounced save for user input data
  useEffect(() => {
    if (!user) return;
    const timeoutId = setTimeout(() => {
      saveUserData({
        userName,
        userTitle,
        socialHandles,
        profileUrl,
        showProfilePic,
        profilePicSource
      });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [userName, userTitle, socialHandles, profileUrl, showProfilePic, profilePicSource, user]);

  // Load admin config from Firestore instead of Express server
  useEffect(() => {
    const loadAdminConfig = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'config', 'admin'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (typeof data.aiEnabled === 'boolean') {
            setAiEnabled(data.aiEnabled);
          }
        }
      } catch (err) {
        console.error('Error fetching admin config:', err);
      }
    };
    loadAdminConfig();
  }, []);

  const bannerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Responsive scaling for the preview
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const baseWidth = bannerSize === 'horizontal' ? 1584 : 1080;
        const newScale = containerWidth / baseWidth;
        setPreviewScale(newScale);
      }
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.clientHeight || (bannerSize === 'horizontal' ? 396 : bannerSize === 'square' ? 1080 : 1920));
      }
    };

    updateScale();
    
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateScale);
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Also observe the banner itself for height changes when badges are added
    if (bannerRef.current) {
      resizeObserver.observe(bannerRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedBadges, layout, fontFamily, selectedTheme, bannerSize]);

  const executeFetch = async (targetUrl: string) => {
    if (!targetUrl.trim()) return;

    setIsFetching(true);
    setFetchError('');

    try {
      const res = await fetch('/api/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileUrl: targetUrl })
      });

      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to fetch profile');
        } else {
          throw new Error(`Failed to fetch profile (${res.status})`);
        }
      }

      const data = await res.json();
      
      if (data.userName) {
        setUserName(data.userName);
        saveUserData({ userName: data.userName });
      }

      if (data.avatarSrc) {
        setFetchedAvatarSrc(data.avatarSrc);
      } else {
        setFetchedAvatarSrc('');
      }
      
      if (data.badges && Array.isArray(data.badges)) {
        setAvailableBadges(data.badges);
        // Auto-select up to 5 most recent badges
        setSelectedBadges(data.badges.slice(0, 5));
      } else {
        setAvailableBadges([]);
        setSelectedBadges([]);
      }

      // Add to recent URLs
      const updatedUrls = [targetUrl, ...recentUrls.filter(u => u !== targetUrl)].slice(0, 5);
      setRecentUrls(updatedUrls);
      saveUserData({ recentUrls: updatedUrls, profileUrl: targetUrl });
    } catch (err: any) {
      console.error(err);
      setFetchError(err.message || 'An error occurred fetching the profile.');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    await executeFetch(profileUrl);
  };

  const toggleBadge = (badge: Badge) => {
    setSelectedBadges(prev => 
      prev.find(b => b.id === badge.id)
        ? prev.filter(b => b.id !== badge.id)
        : [...prev, badge]
    );
  };

  const handleExport = async () => {
    if (!bannerRef.current || isExporting) return;
    
    setIsExporting(true);
    try {
      // Temporarily set transform to scale(1) to ensure crisp 1584x396 capture without cutoffs
      const wrapperEl = bannerRef.current.parentElement;
      const originalTransform = wrapperEl ? wrapperEl.style.transform : '';
      if (wrapperEl) {
        wrapperEl.style.transform = 'scale(1)';
      }
      
      // Wait a moment for layout to settle after transform
      await new Promise(resolve => setTimeout(resolve, 100));

      const options = {
        pixelRatio: exportFormat === 'jpeg' ? 2 : 1, // Double resolution for high-quality JPG, 1x for PNG
        width: bannerSize === 'horizontal' ? 1584 : 1080,
        height: bannerSize === 'horizontal' ? 396 : bannerSize === 'square' ? 1080 : 1920,
      };

      const dataUrl = exportFormat === 'png' 
        ? await htmlToImage.toPng(bannerRef.current, options)
        : await htmlToImage.toJpeg(bannerRef.current, { ...options, quality: 0.95 });
      
      // Restore original scale
      if (wrapperEl) {
        wrapperEl.style.transform = originalTransform;
      }
      
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `prism-canvas-${userName ? userName.replace(/\\s+/g, '-').toLowerCase() : 'pro'}.${exportFormat === 'png' ? 'png' : 'jpg'}`;
      link.click();
    } catch (err) {
      console.error('Failed to export banner:', err);
      alert('Failed to generate banner. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const applyPreset = (preset: IndustryPreset) => {
    const theme = THEMES.find(t => t.id === preset.themeId);
    if (theme) setSelectedTheme(theme);
    
    const pattern = PATTERNS.find(p => p.id === preset.patternId);
    if (pattern) setSelectedPattern(pattern);
    
    setLayout(preset.layout);
    setFontFamily(preset.fontFamily || 'sans');
    setCustomImageUrl('');
    
    if (preset.titlePattern && (!userTitle || userTitle === 'Cloud Learner & Technologist')) {
      setUserTitle(preset.titlePattern);
    }
  };

  const generateAiBackground = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setAiError('Please sign in to generate AI backgrounds.');
      return;
    }

    setIsGeneratingAi(true);
    setAiError('');

    try {
      // Check generation limit
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const todayDate = new Date().toISOString().split('T')[0];
      
      let currentGenerations = 0;
      let lastGenerationDate = '';

      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.aiGenerations) {
          lastGenerationDate = data.aiGenerations.date;
          currentGenerations = data.aiGenerations.count || 0;
        }
      }

      if (lastGenerationDate === todayDate && currentGenerations >= 50) {
        throw new Error('You have reached the daily limit of 50 AI generations. Please try again tomorrow.');
      }

      // Build AI image URL directly on the client (no server needed)
      let themeColors = 'vibrant colors';
      if (selectedTheme?.id === 'orbit-dark') themeColors = 'deep space black with glowing neon cyan, magenta, and purple accents';
      else if (selectedTheme?.id === 'cloud-light') themeColors = 'soft white and light gray with subtle pastel blue and soft gold gradients';
      else if (selectedTheme?.id === 'hacker') themeColors = 'pitch black with bright glowing matrix green accents and neon details';
      else if (selectedTheme?.id === 'midnight-purple') themeColors = 'deep dark purple, indigo, and violet with glowing pink highlights';

      const generatedPrompt = `An abstract, clean, professional background banner for a ${userTitle || 'Professional'}. Color palette: ${themeColors}. Pattern style: ${selectedPattern?.name || 'Geometric'}. No text, no letters, no words. Highly detailed, 8k resolution, abstract digital art, minimalist.`;
      
      let width = 1584;
      let height = 396;
      if (bannerSize === 'square') {
        width = 1080;
        height = 1080;
      } else if (bannerSize === 'vertical') {
        width = 1080;
        height = 1920;
      }
      
      const imageUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(generatedPrompt)}?width=${width}&height=${height}&nologo=true`;
      
      // Update generation limit in Firestore
      const newCount = lastGenerationDate === todayDate ? currentGenerations + 1 : 1;
      await setDoc(userRef, {
        aiGenerations: {
          date: todayDate,
          count: newCount
        }
      }, { merge: true });

      // Preload image so we show loading state until it's actually loaded
      const img = new Image();
      img.onload = () => {
        setCustomImageUrl(imageUrl);
        setIsGeneratingAi(false);
      };
      img.onerror = () => {
        setAiError('Failed to load the generated image. Please try again.');
        setIsGeneratingAi(false);
      };
      img.src = imageUrl;
    } catch (err: any) {
      console.error(err);
      setAiError(err.message || 'An error occurred during AI generation.');
      setIsGeneratingAi(false);
    }
  };

  const handleReorderBadges = (sourceId: string, targetId: string) => {
    setSelectedBadges(prev => {
      const sourceIndex = prev.findIndex(b => b.id === sourceId);
      const targetIndex = prev.findIndex(b => b.id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return prev;
      
      const newBadges = [...prev];
      const [movedBadge] = newBadges.splice(sourceIndex, 1);
      newBadges.splice(targetIndex, 0, movedBadge);
      return newBadges;
    });
  };

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Error signing in', err);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error signing out', err);
    }
  };

  if (loading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center font-sans", isDarkMode ? "dark bg-zinc-950 text-white" : "bg-zinc-50 text-zinc-900")}>
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <LandingPage 
        onSignIn={handleSignIn} 
        isDarkMode={isDarkMode} 
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)} 
      />
    );
  }

  return (
    <div className={cn("min-h-screen xl:h-screen flex flex-col font-sans transition-colors duration-200", isDarkMode ? "dark bg-zinc-950 text-zinc-50" : "bg-zinc-50 text-zinc-900")}>
      {/* Header */}
      <header className="bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800/50 px-6 sm:px-10 py-5 flex items-center justify-between shrink-0 sticky top-0 z-50 transition-colors duration-200">
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="Orbit of Ops" className="w-12 h-12 rounded-2xl object-cover shadow-sm" />
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-zinc-900 dark:text-white leading-none mb-1" style={{ fontFamily: 'var(--font-display)' }}>Prism Canvas <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">by orbitofops</span></h1>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 font-semibold tracking-widest uppercase">Brand Builder</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 ml-auto">
          <form onSubmit={fetchProfile} className="hidden lg:flex items-center gap-3 max-w-md w-full">
            <div className="relative flex-1 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-zinc-900 dark:group-focus-within:text-white transition-colors" />
              <input 
                type="url" 
                placeholder="Paste Google Cloud Skills Boost URL..."
                value={profileUrl}
                onChange={(e) => setProfileUrl(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-zinc-100 dark:bg-zinc-900/50 border-transparent focus:bg-white dark:focus:bg-zinc-900 border focus:border-zinc-300 dark:focus:border-zinc-700 focus:ring-4 focus:ring-zinc-900/5 dark:focus:ring-white/5 rounded-xl text-sm transition-all outline-none font-medium text-zinc-700 dark:text-zinc-200 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                required
              />
            </div>
            <button 
              type="submit" 
              disabled={isFetching}
              className="px-6 py-2.5 bg-zinc-900 dark:bg-white hover:bg-black dark:hover:bg-zinc-200 disabled:bg-zinc-300 dark:disabled:bg-zinc-700 text-white dark:text-zinc-900 font-semibold rounded-xl text-sm transition-all flex items-center gap-2 shadow-sm"
            >
              {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isFetching ? 'Fetching' : 'Fetch'}
            </button>
          </form>
          
          <div className="flex items-center gap-2 border-l border-zinc-200 dark:border-zinc-800 pl-4 ml-2">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-3 bg-zinc-100 dark:bg-zinc-900/50 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-xl transition-colors"
              title="Toggle theme"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={handleSignOut}
              className="p-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-colors"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col xl:flex-row w-full xl:overflow-hidden">
        
        {/* Top/Left Column: Controls */}
        <div className="w-full xl:w-[480px] shrink-0 bg-white dark:bg-zinc-950 xl:overflow-y-auto border-b xl:border-b-0 xl:border-r border-zinc-200 dark:border-zinc-800/50 transition-colors duration-200">
          <div className="p-6 sm:p-8 space-y-10">

            {/* Mobile Fetch Profile */}
            <section className="lg:hidden space-y-4">
               <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" />
                Import Profile
              </h2>
              <form onSubmit={fetchProfile} className="flex flex-col gap-3">
                <input 
                  type="url" 
                  placeholder="Paste Cloud Skills Boost URL..."
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 focus:border-zinc-100 dark:zinc-800/500 dark:focus:border-zinc-100 dark:zinc-800/500 focus:ring-4 focus:ring-zinc-100 dark:zinc-800/500/10 rounded-xl text-sm transition-all outline-none font-medium dark:text-white dark:placeholder:text-zinc-500"
                  required
                />
                <button 
                  type="submit" 
                  disabled={isFetching}
                  className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 hover:bg-black dark:hover:bg-black dark:hover:bg-white dark:hover:text-black disabled:bg-zinc-400 dark:disabled:bg-zinc-700 text-white font-semibold rounded-xl text-sm flex items-center justify-center gap-2 shadow-sm transition-colors"
                >
                  {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Badges'}
                </button>
              </form>
            </section>

            {fetchError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm font-medium rounded-xl border border-red-100 dark:border-red-900/50 flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                {fetchError}
              </div>
            )}

            {/* Recent Profiles */}
            {recentUrls.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                    <History className="w-3.5 h-3.5" />
                    Recent Profiles
                  </h2>
                  <button 
                    onClick={() => {
                      setRecentUrls([]);
                      localStorage.removeItem('recentProfileUrls');
                    }}
                    className="text-xs font-medium text-zinc-500 hover:text-red-500 transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentUrls.map(url => {
                    let displayUrl = url;
                    try {
                      const urlObj = new URL(url);
                      const pathParts = urlObj.pathname.split('/').filter(Boolean);
                      if (pathParts.length > 0) {
                        displayUrl = pathParts[pathParts.length - 1];
                      } else {
                        displayUrl = urlObj.hostname;
                      }
                    } catch {
                      displayUrl = url;
                    }
                    return (
                      <button
                        key={url}
                        onClick={() => {
                          setProfileUrl(url);
                          executeFetch(url);
                        }}
                        className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-100 dark:zinc-800/50 hover:text-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800/30 dark:hover:text-zinc-400 text-zinc-600 dark:text-zinc-300 text-xs font-medium rounded-lg transition-colors truncate max-w-[200px]"
                        title={url}
                      >
                        {displayUrl}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            
            {/* User Details */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <LayoutTemplate className="w-3.5 h-3.5" />
                Identity
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">Display Name</label>
                  <input 
                    type="text" 
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="e.g. Alex Chen"
                    className="w-full px-4 py-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl focus:ring-4 focus:ring-zinc-100 dark:zinc-800/500/10 focus:border-zinc-100 dark:zinc-800/500 dark:focus:border-zinc-100 dark:zinc-800/500 outline-none transition-all font-medium text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">Professional Headline</label>
                  <input 
                    type="text" 
                    value={userTitle}
                    onChange={(e) => setUserTitle(e.target.value)}
                    placeholder="e.g. Cloud Engineer | 3x Certified"
                    className="w-full px-4 py-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl focus:ring-4 focus:ring-zinc-100 dark:zinc-800/500/10 focus:border-zinc-100 dark:zinc-800/500 dark:focus:border-zinc-100 dark:zinc-800/500 outline-none transition-all font-medium text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                  <div className="relative flex items-center">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={showProfilePic}
                      onChange={(e) => setShowProfilePic(e.target.checked)}
                    />
                    <div className="w-10 h-6 bg-zinc-200 dark:bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 dark:after:border-zinc-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900 dark:bg-zinc-100"></div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Include Profile Picture</div>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Display your avatar on the banner</div>
                  </div>
                </label>

                {showProfilePic && (
                  <div className="p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-50 dark:bg-zinc-800/20">
                    <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-2">Picture Source</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setProfilePicSource('google')}
                        className={cn(
                          "flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-colors border",
                          profilePicSource === 'google' 
                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 border-transparent" 
                            : "bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                        )}
                      >
                        Google Account
                      </button>
                      <button
                        onClick={() => setProfilePicSource('skills')}
                        disabled={!fetchedAvatarSrc}
                        className={cn(
                          "flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-colors border",
                          profilePicSource === 'skills' 
                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 border-transparent" 
                            : "bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                      >
                        Cloud Skills Boost
                      </button>
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 cursor-pointer p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                  <div className="relative flex items-center">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={showQrCode}
                      onChange={(e) => setShowQrCode(e.target.checked)}
                      disabled={!profileUrl}
                    />
                    <div className="w-10 h-6 bg-zinc-200 dark:bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 dark:after:border-zinc-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900 dark:bg-zinc-100"></div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Include QR Code</div>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Links to your Cloud Skills profile</div>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                  <div className="relative flex items-center">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={showSocials}
                      onChange={(e) => setShowSocials(e.target.checked)}
                    />
                    <div className="w-10 h-6 bg-zinc-200 dark:bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 dark:after:border-zinc-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-zinc-900 dark:bg-zinc-100"></div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Include Social Links</div>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Display social handles on banner</div>
                  </div>
                </label>

                {showSocials && (
                  <div className="space-y-4 p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-50 dark:bg-zinc-800/20">
                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">LinkedIn Handle</label>
                      <input 
                        type="text" 
                        value={socialHandles.linkedin || ''}
                        onChange={(e) => setSocialHandles({...socialHandles, linkedin: e.target.value})}
                        placeholder="e.g. alexchen"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg focus:ring-2 focus:ring-zinc-100 dark:zinc-800/500/10 focus:border-zinc-100 dark:zinc-800/500 outline-none transition-all font-medium text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">GitHub Handle</label>
                      <input 
                        type="text" 
                        value={socialHandles.github || ''}
                        onChange={(e) => setSocialHandles({...socialHandles, github: e.target.value})}
                        placeholder="e.g. alexchen-dev"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg focus:ring-2 focus:ring-zinc-100 dark:zinc-800/500/10 focus:border-zinc-100 dark:zinc-800/500 outline-none transition-all font-medium text-zinc-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1.5">Twitter / X Handle</label>
                      <input 
                        type="text" 
                        value={socialHandles.twitter || ''}
                        onChange={(e) => setSocialHandles({...socialHandles, twitter: e.target.value})}
                        placeholder="e.g. @alexchen"
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg focus:ring-2 focus:ring-zinc-100 dark:zinc-800/500/10 focus:border-zinc-100 dark:zinc-800/500 outline-none transition-all font-medium text-zinc-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Industry Presets Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5" />
                Industry Presets
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    title={`Apply ${preset.name} preset styling and layouts`}
                    className="px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:border-zinc-100 dark:zinc-800/500 hover:bg-zinc-100 dark:zinc-800/50 dark:hover:bg-zinc-800/20 text-left transition-all group"
                  >
                    <div className="font-bold text-sm text-zinc-900 dark:text-white group-hover:text-zinc-900 dark:text-zinc-100 dark:group-hover:text-zinc-400 transition-colors">{preset.name}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Size Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <Crop className="w-3.5 h-3.5" />
                Dimension
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {(['horizontal', 'square', 'vertical'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setBannerSize(s)}
                    className={cn(
                      "px-4 py-3 rounded-xl border-2 text-center transition-all relative overflow-hidden group capitalize",
                      bannerSize === s 
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20" 
                        : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    )}
                  >
                    <div className={cn("font-bold text-sm", bannerSize === s ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-300")}>{s}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Layout Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <LayoutTemplate className="w-3.5 h-3.5" />
                Layout
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {(['compact', 'centric'] as const).map(l => (
                  <button
                    key={l}
                    onClick={() => setLayout(l)}
                    title={l === 'compact' ? 'Aligns badges and text to the right, leaving space for the profile picture' : 'Centers all elements for a balanced, symmetrical look'}
                    className={cn(
                      "px-5 py-4 rounded-2xl border-2 text-left transition-all relative overflow-hidden group capitalize",
                      layout === l 
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20" 
                        : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    )}
                  >
                    <div className={cn("font-bold text-sm", layout === l ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-300")}>{l}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      {l === 'compact' ? 'Right-aligned badges' : 'Centered badges'}
                    </div>
                    {layout === l && (
                      <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-zinc-900 dark:bg-zinc-100" />
                    )}
                  </button>
                ))}
              </div>
            </section>

            {/* Typography Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <Type className="w-3.5 h-3.5" />
                Typography
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {(['sans', 'display', 'mono', 'serif'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setFontFamily(f)}
                    title={f === 'sans' ? 'Clean, versatile, and modern (Inter)' : f === 'display' ? 'Bold and tech-forward (Space Grotesk)' : f === 'mono' ? 'Technical, code-like appearance (JetBrains Mono)' : 'Elegant, editorial serif style (Playfair Display)'}
                    className={cn(
                      "px-4 py-3 rounded-xl border-2 text-center transition-all relative overflow-hidden group capitalize",
                      fontFamily === f 
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20 text-zinc-900 dark:text-zinc-100 dark:text-zinc-400" 
                        : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    )}
                  >
                    <span className="font-bold text-sm" style={{ fontFamily: `var(--font-${f})` }}>{f}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Theme Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5" />
                Aesthetic
              </h2>

              {aiEnabled && (
                <div className="p-5 border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/10 rounded-2xl mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-black dark:text-zinc-200">✨ AI Background Generator</span>
                  </div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-4 font-medium">
                    Let AI create a custom background based on your title, selected aesthetic, and pattern.
                  </div>
                  <form onSubmit={generateAiBackground} className="flex flex-col gap-3">
                    <button 
                      type="submit" 
                      disabled={isGeneratingAi}
                      className="w-full py-2.5 bg-zinc-900 dark:bg-zinc-100 hover:bg-black dark:hover:bg-white disabled:bg-zinc-200 dark:disabled:bg-zinc-800 disabled:cursor-not-allowed text-white dark:text-zinc-900 disabled:text-zinc-500 dark:disabled:text-zinc-400 font-semibold rounded-xl text-sm flex items-center justify-center gap-2 transition-all shadow-sm"
                    >
                      {isGeneratingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {isGeneratingAi ? 'Generating...' : 'Generate with AI'}
                    </button>
                    {aiError && <div className="text-xs text-red-500 mt-1">{aiError}</div>}
                  </form>
                  {customImageUrl && (
                    <div className="mt-3 flex justify-between items-center text-xs font-medium text-zinc-900 dark:text-zinc-100 dark:text-zinc-400">
                      <span>Custom Image Applied</span>
                      <button onClick={() => setCustomImageUrl('')} className="underline hover:text-zinc-800 dark:hover:text-zinc-300">Clear</button>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {THEMES.map(theme => (
                  <button
                    key={theme.id}
                    onClick={() => { setSelectedTheme(theme); setCustomImageUrl(''); }}
                    title={theme.id === 'orbit-dark' ? 'A deep, high-contrast dark mode with glowing accents' : theme.id === 'cloud-light' ? 'Clean and minimalist light design' : theme.id === 'hacker' ? 'Monochrome terminal-inspired aesthetic' : 'Rich dark theme with subtle gradients'}
                    className={cn(
                      "px-5 py-4 rounded-2xl border-2 text-left transition-all relative overflow-hidden group",
                      selectedTheme.id === theme.id && !customImageUrl
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20" 
                        : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    )}
                  >
                    <div className={cn("font-bold text-sm", selectedTheme.id === theme.id && !customImageUrl ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-300")}>{theme.name}</div>
                    {selectedTheme.id === theme.id && !customImageUrl && (
                      <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-zinc-900 dark:bg-zinc-100" />
                    )}
                  </button>
                ))}
              </div>
            </section>

            {/* Pattern Selection */}
            <section className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <LayoutTemplate className="w-3.5 h-3.5" />
                Pattern
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {PATTERNS.map(pattern => (
                  <button
                    key={pattern.id}
                    onClick={() => { setSelectedPattern(pattern); setCustomImageUrl(''); }}
                    className={cn(
                      "px-5 py-4 rounded-2xl border-2 text-left transition-all relative overflow-hidden group",
                      selectedPattern.id === pattern.id && !customImageUrl
                        ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20" 
                        : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    )}
                  >
                    <div className={cn("font-bold text-sm", selectedPattern.id === pattern.id && !customImageUrl ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-300")}>{pattern.name}</div>
                    {selectedPattern.id === pattern.id && !customImageUrl && (
                      <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-zinc-900 dark:bg-zinc-100" />
                    )}
                  </button>
                ))}
              </div>
            </section>

            {/* Badge Selection */}
            <section className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5" />
                  Achievements
                </h2>
                <span className="text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 px-3 py-1 rounded-full transition-colors">
                  {selectedBadges.length} Selected
                </span>
              </div>
              
              {availableBadges.length === 0 ? (
                <div className="p-10 text-center border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 flex flex-col items-center justify-center gap-3 transition-colors">
                  <Shield className="w-10 h-10 text-zinc-300 dark:text-zinc-600" />
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400 max-w-[200px]">
                    Fetch your profile above to load your badges.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {availableBadges.map(badge => {
                    const isSelected = selectedBadges.some(b => b.id === badge.id);
                    return (
                      <button
                        key={badge.id}
                        onClick={() => toggleBadge(badge)}
                        className={cn(
                          "flex flex-col items-center justify-center p-3 rounded-xl border-2 text-center transition-all text-xs group relative",
                          isSelected
                            ? "border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800/50 dark:bg-zinc-800/20"
                            : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        )}
                      >
                        {badge.imageUrl ? (
                          <img src={badge.imageUrl} alt={badge.name} className="w-10 h-10 object-contain drop-shadow-sm mb-2" crossOrigin="anonymous" />
                        ) : (
                          <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 rounded-lg flex items-center justify-center transition-colors mb-2">
                            <Shield className="w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                          </div>
                        )}
                        <span className={cn("font-bold line-clamp-2 transition-colors", isSelected ? "text-zinc-800 dark:text-zinc-100" : "text-zinc-700 dark:text-zinc-300")}>
                          {badge.name}
                        </span>
                        
                        <div className={cn(
                          "absolute top-2 right-2 w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all",
                          isSelected ? "bg-zinc-900 dark:bg-zinc-100 border-zinc-900 dark:border-zinc-100 text-white" : "bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-transparent group-hover:border-zinc-400 dark:group-hover:border-zinc-600"
                        )}>
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

          </div>
        </div>

        {/* Right/Bottom Column: Preview & Export */}
        <div className="flex-1 bg-zinc-50 dark:bg-zinc-950 p-6 sm:p-10 lg:p-16 flex flex-col relative xl:overflow-y-auto transition-colors duration-200">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px] opacity-60 dark:opacity-40 pointer-events-none transition-colors" />

          <div className="max-w-[1200px] w-full mx-auto space-y-8 relative z-10">
            
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
              <div>
                <h2 className="text-3xl font-extrabold text-zinc-900 dark:text-white tracking-tight mb-2 transition-colors">Live Preview</h2>
                <p className="text-zinc-500 font-medium">Auto-scales for export ({bannerSize === 'horizontal' ? 1584 : 1080} × {bannerSize === 'horizontal' ? 396 : bannerSize === 'square' ? 1080 : 1920}px)</p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center bg-zinc-100 dark:bg-zinc-800/50 p-1 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-800">
                  <button
                    onClick={() => setExportFormat('png')}
                    className={cn(
                      "px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex-1 sm:flex-none",
                      exportFormat === 'png' 
                        ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm" 
                        : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                    )}
                  >
                    PNG
                  </button>
                  <button
                    onClick={() => setExportFormat('jpeg')}
                    className={cn(
                      "px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex-1 sm:flex-none",
                      exportFormat === 'jpeg' 
                        ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm" 
                        : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                    )}
                  >
                    HQ JPG
                  </button>
                </div>
                
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className={cn(
                    "flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-white shadow-md transition-all text-base",
                    isExporting 
                      ? "bg-zinc-300 dark:bg-zinc-800 text-zinc-500 cursor-not-allowed shadow-none" 
                      : "bg-zinc-900 dark:bg-white dark:text-zinc-900 hover:bg-black dark:hover:bg-zinc-200 hover:-translate-y-1 active:translate-y-0"
                  )}
                >
                  {isExporting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  {isExporting ? 'Generating...' : `Download ${exportFormat.toUpperCase()}`}
                </button>
              </div>
            </div>

            {/* Preview Container */}
            <div className="w-full bg-white dark:bg-zinc-950 rounded-[2rem] shadow-sm border border-zinc-200 dark:border-zinc-800 p-2 sm:p-4 transition-colors duration-200">
              <div 
                ref={containerRef} 
                className="w-full rounded-[1.5rem] bg-zinc-100 dark:bg-zinc-900 relative overflow-hidden transition-colors"
                style={{ height: `${bannerHeight * previewScale}px` }}
              >
                {/* The actual banner, scaled down via CSS transform */}
                <BannerPreview
                  ref={bannerRef}
                  theme={selectedTheme}
                  pattern={selectedPattern}
                  selectedBadges={selectedBadges}
                  userName={userName}
                  userTitle={userTitle}
                  scale={previewScale}
                  bannerSize={bannerSize}
                  showQrCode={showQrCode}
                  profileUrl={profileUrl}
                  layout={layout}
                  fontFamily={fontFamily}
                  showSocials={showSocials}
                  socialHandles={socialHandles}
                  customImageUrl={customImageUrl}
                  isGeneratingAi={isGeneratingAi}
                  onReorderBadges={handleReorderBadges}
                  showProfilePic={showProfilePic}
                  profilePicSrc={profilePicSource === 'google' ? user?.photoURL || '' : fetchedAvatarSrc}
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="text-center pt-8 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-100 dark:zinc-800/50 dark:bg-zinc-800/30 border border-zinc-100 dark:border-zinc-800 text-black dark:white dark:text-zinc-300 font-semibold text-sm mb-4 transition-colors">
                <LayoutTemplate className="w-4 h-4" />
                Pro Tip
              </div>
              <p className="text-base font-medium text-zinc-500 dark:text-zinc-400 leading-relaxed transition-colors">
                LinkedIn overlays your profile picture on the bottom-left corner of the banner. 
                Our templates are specifically designed with a safe zone to ensure your content is never hidden.
              </p>
            </div>

          </div>
        </div>

      </main>
    </div>
  );
}
